import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { Button } from "@/components/ui/button.tsx";
import { useLocalStorage } from "@/hooks/useLocalStorage.ts";

import { userTaskQueries } from "@/components/userTasks/api.ts";
import { ThreadUserTaskList } from "@/components/userTasks/ThreadUserTasks.tsx";
import { useTaskGates } from "@/components/userTasks/useTaskGates.ts";
import { TaskList } from "./TaskList.tsx";
import { ThreadTasksDialog } from "./ThreadTasksDialog.tsx";
import { taskQueries } from "./useTasks.ts";

/**
 * Whether the composer's task list is showing. One key for the composer rather
 * than one per thread: it is a choice about how much of the composer the user
 * wants spent on tasks, not a fact about any one conversation.
 */
const EXPANDED_STORAGE_KEY = "omnibox-tasks-expanded";

/** Past this the badge stops being readable at 10px, and the list is the answer. */
const MAX_BADGE_COUNT = 9;

/**
 * What the badge reads: the count, or `9+` once a two-digit number stops fitting
 * the 10px pill it sits in. Exported because the badge moved into the composer's
 * tools menu, and the number and the ceiling that makes it legible belong
 * together wherever it is drawn.
 */
export const openTaskBadgeLabel = (openCount: number) =>
  openCount > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : String(openCount);

/**
 * What the toggle is called. The badge is capped because a two-digit number
 * stops fitting; the name is not, because nothing is being read aloud in 10px —
 * so a screen reader hears "Tasks (12 open)" where the pill shows `9+`.
 */
export const openTaskToggleLabel = (openCount: number) =>
  openCount > 0 ? `Tasks (${openCount} open)` : "Tasks";

/**
 * How many open tasks the composer shows before deferring to the full list.
 * The panel sits directly above the input, so it is a glance at what's
 * outstanding rather than the record — past a few rows it stops being either.
 * The badge still counts them all, and "All" says how many the dialog holds.
 *
 * The dialogs are uncapped; this is the composer's limit alone.
 */
const VISIBLE_TASK_LIMIT = 3;

/** Where "All" stops counting, since past it the exact number changes nothing. */
const MAX_ALL_COUNT = 10;

export type OmniboxTasks = ReturnType<typeof useOmniboxTasks>;

/**
 * The thread's open tasks, for the two slots the omnibox gives them: a count on
 * a toolbar toggle and a compact list at the top of the card. One hook because
 * the two slots are in different rows of the same form and share their state.
 *
 * The query runs for as long as the composer does — the badge has to be right
 * before anyone clicks anything — so `enabled` gates the request, not just what
 * renders. The app-level task socket listener keeps the shared cache current.
 */
export const useOmniboxTasks = ({
  threadId,
  enabled,
}: {
  threadId: number | undefined;
  /** The caller's own reason to opt out — a composer with no task chrome. */
  enabled: boolean;
}) => {
  // With `tasks-v2` the thread's tasks come from the user's list.
  const { list: onList, any: tasksEnabled } = useTaskGates();
  // Truthy, not merely defined: the debug view mounts a composer over a mock
  // thread whose id is 0, and `taskQueries.threadTasks` reads the same
  // truthiness to fall back to `skipToken`. Testing for `undefined` alone would
  // render a control over a query that can never resolve.
  const isOn = enabled && tasksEnabled && !!threadId;
  const activeThreadId = isOn ? threadId : undefined;

  const perThread = useQuery(taskQueries.threadTasks(onList ? undefined : activeThreadId));
  const listed = useQuery(userTaskQueries.thread(onList ? activeThreadId : undefined));
  const { isError, isLoading, refetch } = onList ? listed : perThread;

  const [isExpanded, setIsExpanded] = useLocalStorage<boolean>(EXPANDED_STORAGE_KEY, false);
  const [dialogThreadId, setDialogThreadId] = useState<number>();
  const isDialogOpen = activeThreadId !== undefined && dialogThreadId === activeThreadId;
  const setIsDialogOpen = useCallback(
    (open: boolean) => setDialogThreadId(open ? activeThreadId : undefined),
    [activeThreadId],
  );

  // Rebuilt on every render otherwise — including each socket-driven
  // invalidation, which is most of them here — defeating `TaskList`'s memo.
  const openTasks = useMemo(
    () => (perThread.data?.tasks ?? []).filter((item) => item.task.status === "open"),
    [perThread.data],
  );
  const visibleTasks = useMemo(() => openTasks.slice(0, VISIBLE_TASK_LIMIT), [openTasks]);
  const openListed = useMemo(
    () => (listed.data?.tasks ?? []).filter((task) => task.status === "open"),
    [listed.data],
  );
  const visibleListed = useMemo(() => openListed.slice(0, VISIBLE_TASK_LIMIT), [openListed]);

  const toggleExpanded = () => setIsExpanded((wasExpanded) => !wasExpanded);
  const handleRetry = useCallback(() => void refetch(), [refetch]);

  return {
    enabled: isOn,
    threadId: activeThreadId,
    /** Every open task — what the badge counts. */
    openCount: onList ? openListed.length : openTasks.length,
    /** Every task on the thread, whatever its status — what "All" opens onto. */
    totalCount: (onList ? listed.data?.tasks.length : perThread.data?.tasks.length) ?? 0,
    /** The first {@link VISIBLE_TASK_LIMIT} of them — what the panel lists. */
    visibleTasks,
    /** The same, from the user's list, when the thread's tasks live there. */
    visibleListed: onList ? visibleListed : undefined,
    isExpanded,
    toggleExpanded,
    isDialogOpen,
    setIsDialogOpen,
    isLoading,
    isError,
    handleRetry,
  };
};

/**
 * The open tasks themselves, at the top of the omnibox card, plus the way
 * through to the full record.
 *
 * Renders nothing at all when collapsed rather than an empty box: the omnibox
 * form is a `gap-3` column, so a box with no content in it still shows, as a
 * gap above the input on every thread.
 */
export const OmniboxTasksPanel = ({ tasks }: { tasks: OmniboxTasks }) => {
  const {
    enabled,
    threadId,
    visibleTasks,
    visibleListed,
    totalCount,
    isExpanded,
    isDialogOpen,
    setIsDialogOpen,
    isLoading,
    isError,
    handleRetry,
  } = tasks;

  if (!enabled || !isExpanded || threadId === undefined) return null;

  // Nothing outstanding is a one-line answer, so it shares the line with the way
  // through to the full list rather than taking a row of the composer to itself.
  const isEmpty = !isLoading && !isError && (visibleListed ?? visibleTasks).length === 0;

  return (
    <div className="flex flex-col gap-0.5 border-b pb-1.5">
      {/* No scroller: the list is capped at {@link VISIBLE_TASK_LIMIT}, so it
          can't grow enough to need one — which is the point, since a composer
          that walks off the top of the screen is worse than a short list with a
          way through to the rest. */}
      {!isEmpty && visibleListed && (
        <ThreadUserTaskList
          tasks={visibleListed}
          isLoading={isLoading}
          isError={isError}
          onRetry={handleRetry}
        />
      )}
      {!isEmpty && !visibleListed && (
        <TaskList
          tasks={visibleTasks}
          isLoading={isLoading}
          isError={isError}
          onRetry={handleRetry}
          showThread={false}
          compact
        />
      )}
      {/* No inset: this row's text lines up with the textarea's left edge below
          it, and "All" with the send button's right edge. */}
      <div className="flex items-center gap-2">
        {isEmpty && <span className="text-xs text-muted-foreground">No open tasks.</span>}
        <ThreadTasksDialog
          threadId={threadId}
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          trigger={
            <Button
              type="button"
              variant="link"
              size="fit"
              className="ml-auto h-auto p-0 text-xs"
              data-statsig-name="omnibox-tasks-all"
            >
              {/* The count names what clicking gets you — every status, which is
                  more than the open tasks listed above and than the badge. */}
              All ({totalCount > MAX_ALL_COUNT ? `${MAX_ALL_COUNT}+` : totalCount})
            </Button>
          }
        />
      </div>
    </div>
  );
};
